import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Paper, IconButton, Typography } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { useActiveInvestigation } from '../ResearchAgentTray/hooks/useActiveInvestigation'
import { useUtilityPanel, LAYOUT_PRESETS } from './UtilityPanelProvider'
import UtilityPanelToolbar from './UtilityPanelToolbar'
import { TOOL_REGISTRY } from './tools/toolRegistry'
import { useUtilityPanelStyles } from './utilityPanelStyles'

const MIN_PANEL_FRACTION = 0.15
const MAX_PANEL_FRACTION = 0.85

export default function UtilityPanel () {
  const classes = useUtilityPanelStyles()
  const {
    isOpen,
    activeToolId,
    toolPayload,
    closeTool,
    layoutMode,
    customRatio,
    setCustomRatio,
    effectivePreset,
    headerMeta,
    actions
  } = useUtilityPanel()

  // Drives the left-border pulse while a research investigation is in
  // flight. Wired here (not inside ResearchTool) so the cue is visible
  // even when another tool is foreground or the panel is closed-but-
  // about-to-reopen. Other tools can later add their own busy signals
  // by extending this OR-chain.
  const { isBusy: researchBusy } = useActiveInvestigation()

  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef(0)
  const containerWidthRef = useRef(0)
  const startPanelFractionRef = useRef(0)
  const panelWrapperRef = useRef(null)

  const handleResizeMouseDown = useCallback((e) => {
    e.preventDefault()
    // Use the panel wrapper's offsetParent (the workArea flex-row) as
    // the reference width. This is the container whose total flex
    // budget the wrapper and the grid share.
    const container = panelWrapperRef.current?.parentElement
    if (!container) return
    containerWidthRef.current = container.offsetWidth
    startXRef.current = e.clientX
    const preset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS.sidebar
    startPanelFractionRef.current = preset.panelFlex / (preset.contentFlex + preset.panelFlex)
    setIsDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [customRatio, layoutMode])

  const handleResizeDoubleClick = useCallback(() => {
    setCustomRatio(null)
  }, [setCustomRatio])

  useEffect(() => {
    if (!isDragging) return undefined

    const handleMouseMove = (e) => {
      const delta = startXRef.current - e.clientX
      const deltaFraction = delta / containerWidthRef.current
      const panelFraction = Math.min(
        MAX_PANEL_FRACTION,
        Math.max(MIN_PANEL_FRACTION, startPanelFractionRef.current + deltaFraction)
      )
      const contentFraction = 1 - panelFraction
      setCustomRatio({
        contentFlex: Math.round(contentFraction * 100),
        panelFlex: Math.round(panelFraction * 100)
      })
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      // Restore body styles in case the panel unmounts mid-drag --
      // mouseup wouldn't fire to do it, and the page would otherwise
      // be stuck in `col-resize` cursor + non-selectable mode.
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isDragging, setCustomRatio])

  if (!isOpen || !activeToolId) return null

  const tool = TOOL_REGISTRY[activeToolId]
  if (!tool) return null

  const ToolComponent = tool.component
  const eyebrow = headerMeta?.eyebrow ?? tool.label
  const title = headerMeta?.title ?? null
  const subjectLine = headerMeta?.subjectLine ?? null

  return (
    <div
      ref={panelWrapperRef}
      className={classes.panelWrapper}
      style={{
        flex: effectivePreset.panelFlex,
        transition: customRatio ? 'none' : undefined
      }}
    >
      <div
        className={`${classes.resizeHandle} ${isDragging ? classes.resizeHandleActive : ''}`}
        onMouseDown={handleResizeMouseDown}
        onDoubleClick={handleResizeDoubleClick}
        aria-label='Resize panel'
        role='separator'
        aria-orientation='vertical'
      >
        <div className={classes.resizeIndicator} />
      </div>

      <Paper
        className={`${classes.panel} ${researchBusy ? classes.panelBusy : ''}`}
        elevation={0}
        square
      >
        <div className={classes.header}>
          {tool.renderIcon && (
            <span className={classes.headerIcon} aria-hidden='true'>
              {tool.renderIcon()}
            </span>
          )}
          <div className={classes.headerText}>
            <Typography component='div' className={classes.eyebrow}>{eyebrow}</Typography>
            {title && <Typography component='div' className={classes.headerTitle}>{title}</Typography>}
            {subjectLine && <Typography component='div' className={classes.headerSubject}>{subjectLine}</Typography>}
          </div>
          <IconButton size='small' onClick={closeTool} aria-label='Close panel'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </div>

        <UtilityPanelToolbar actions={actions} />

        <div className={classes.body}>
          <ToolComponent payload={toolPayload} />
        </div>
      </Paper>
    </div>
  )
}
